// The thinking layer in the app (docs/TERV-kontextus-AI.md): the day's most important things, "Várok rá", a thread's
// living state with its loops, and the owner's instructions (with the ones the AI learned from a correction, to approve).
import { useCallback, useEffect, useState } from 'react';
import { api, onChange } from '../api';
import { ClaudeMark, day, Label, Panel } from '../ui';

const TYPE: Record<string, string> = { my_task: 'teendőd', reply_needed: 'válaszra vár', waiting_for: 'várok rá', promise: 'ígéreted', decision: 'döntened kell', project_task: 'projektfeladat' };
const Imp = ({ n }: { n: number }) => <span className="imp" title={`fontosság ${n}/5`}>{'●'.repeat(n)}<i>{'●'.repeat(5 - n)}</i></span>;
/** "≈ 72 111 Ft · 15 perc": the two values of a loop */
export const MoneyTime = ({ l }: { l: { money?: number | null; money_note?: string | null; minutes?: number | null } }) =>
  l.money == null && l.minutes == null ? null : (
    <span className="mt">{l.money != null && <b title={l.money_note ?? undefined}>≈ {Math.abs(l.money).toLocaleString('hu-HU')} Ft{l.money_note ? ` (${l.money_note})` : ''}</b>}
      {l.money != null && l.minutes != null && ' · '}{l.minutes != null && <span>{l.minutes < 60 ? `${l.minutes} perc` : `${Math.round(l.minutes / 6) / 10} óra`}</span>}</span>);
const plus = (n: number) => new Date(Date.now() + n * 86400000).toLocaleDateString('sv-SE', { timeZone: 'Europe/Budapest' });

/** A ready draft (a reply, a follow-up): shown on a tap, copied with one more. */
export function Draft({ text }: { text: string | null | undefined }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  return (
    <div className="draft">
      <button type="button" className="j-btn small-btn" onClick={() => setOpen(!open)}>{open ? 'Vázlat elrejtése' : 'Kész levél'}</button>
      {open && <><pre>{text}</pre><button type="button" className="j-btn small-btn" onClick={() => navigator.clipboard.writeText(text).then(() => setCopied(true))}>{copied ? 'Kimásolva' : 'Másolás'}</button></>}
    </div>
  );
}

/** "Mondd el a Jarvisnak": a fact from the owner (who paid, what happened), judged in its context like a mail. */
export function TellJarvis() {
  const [text, setText] = useState('');
  const [said, setSaid] = useState('');
  const [err, setErr] = useState('');
  const send = () => api('/api/notes', { text }).then(() => { setSaid(text); setText(''); setErr(''); }).catch((e) => setErr(e.message));
  return (
    <form className="j-row quick-form tell" onSubmit={(e) => { e.preventDefault(); if (text.trim()) send(); }}>
      <input className="j-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Mondd el a Jarvisnak… (pl. „Cselikovics még nem fizetett”)" />
      <button className="j-btn j-btn--primary">Elmondom</button>
      {said && <p className="small muted">Megjegyeztem, beépítem a szál állapotába: „{said.slice(0, 80)}”</p>}
      {err && <p className="err small">{err}</p>}
    </form>
  );
}

export function TodayTop({ top }: { top: { top: Array<{ text: string; why: string; thread_id: string | null }>; note: string | null } | null }) {
  if (!top?.top?.length) return null;
  return (
    <Panel c="amber"><Label c="amber">Ma a legfontosabb</Label>
      <ol className="top3">{top.top.map((x, i) => (
        <li key={i}>{x.thread_id ? <a href={`#/szal/${x.thread_id}`}>{x.text}</a> : x.text}<div className="w">{x.why}</div></li>
      ))}</ol>
      {top.note && <p className="small muted">{top.note}</p>}
    </Panel>
  );
}

type Act = (ref: string, action: 'done' | 'dismiss' | 'snooze', days?: number) => void;

export function Waiting({ items, act }: { items: any[]; act: Act }) {
  if (!items.length) return null;
  return (
    <Panel c="violet"><Label c="violet">Várok rá</Label>
      {items.map((w) => (
        <div className="j-slot" key={w.ref}>
          <div className={`t ${w.chase ? 'late' : ''}`}>{w.chase ? 'sürgesd' : w.follow_up ? day(w.follow_up) : day(w.since)}</div>
          <div><div><b>{w.party || w.thread}</b> · {w.text}</div><MoneyTime l={w} /><div className="w">{w.why}{w.thread ? ` · ${w.thread}` : ''}</div>
            <Draft text={w.draft} />
            <div className="task-acts">
              <button className="j-btn" onClick={() => act(w.ref, 'done')}>Megjött</button>
              <button className="j-btn" onClick={() => act(w.ref, 'snooze', 5)}>+5 nap</button>
              <button className="j-btn" onClick={() => act(w.ref, 'dismiss')}>Nem kell</button>
            </div></div>
        </div>
      ))}
    </Panel>
  );
}

/** A thread's state: where it stands, its turns, its open loops with importance and why. */
export function ThreadState({ doc, loops, reload }: { doc: any; loops: any[]; reload: () => void }) {
  const [err, setErr] = useState('');
  const act: Act = (ref, action, days) => api(`/api/tasks/${ref}`, { action, days }).then(reload).catch((e) => setErr(e.message));
  if (!doc && !loops.length) return null;
  const d = doc?.doc ?? {};
  return (
    <>
      {d.summary && <Panel c="cyan"><Label c="cyan">Hol tart</Label><p>{d.summary}</p>
        {d.risks && <p className="warn">Kockázat: {d.risks}</p>}
        {d.story?.length > 0 && <details className="story"><summary>Fordulatok · {d.story.length}</summary>{d.story.map((s: any, i: number) => <div key={i} className="small"><b>{day(s.date)}</b> {s.text}</div>)}</details>}
        {doc?.created_at && <p className="small muted">Frissítve: {day(doc.created_at)} · {doc.because}</p>}
      </Panel>}
      {loops.length > 0 && <Panel c="coral"><Label c="coral">Nyitott hurkok · {loops.length}</Label>
        {loops.map((l) => (
          <div className="j-slot" key={l.ref}>
            <div className="t">{l.due ? day(l.due) : l.follow_up ? day(l.follow_up) : ''}<Imp n={l.importance} /></div>
            <div><div><em className="loop-type">{TYPE[l.type] ?? l.type}</em> {l.text}{l.party ? ` · ${l.party}` : ''}</div><MoneyTime l={l} />{l.why && <div className="w">{l.why}</div>}
              <Draft text={l.draft} />
              <div className="task-acts">
                <button className="j-btn" onClick={() => act(l.ref, 'done')}>Kész</button>
                <button className="j-btn" onClick={() => act(l.ref, 'snooze', 1)}>Holnapra</button>
                <button className="j-btn" onClick={() => act(l.ref, 'dismiss')}>Nem kell</button>
              </div></div>
          </div>
        ))}
        {err && <p className="err small">{err}</p>}
      </Panel>}
    </>
  );
}

/** The owner's instructions: what the AI must always keep in mind. The learned ones wait for approval. */
export function Guidance({ onlyProposed = false }: { onlyProposed?: boolean }) {
  const [list, setList] = useState<any[] | null>(null);
  const [text, setText] = useState('');
  const [edit, setEdit] = useState<{ id: string; text: string } | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(() => api<any[]>('/api/guidance').then(setList).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); return onChange(load); }, [load]);
  const run = (p: Promise<unknown>) => p.then(load).catch((e) => setErr(e.message));
  const set = (id: string, body: object) => run(api(`/api/guidance/${id}`, body, { method: 'PATCH' }));
  if (!list) return null;
  const proposed = list.filter((g) => g.status === 'proposed');
  if (onlyProposed && !proposed.length) return null;
  const shown = onlyProposed ? proposed : list;
  return (
    <Panel c="amber"><Label c="amber">{onlyProposed ? `Megjegyezzem? · ${proposed.length}` : 'Utasításaid a Jarvisnak'}</Label>
      {!onlyProposed && <p className="small muted">Ezeket a Jarvis minden döntésénél figyelembe veszi. Írd le a saját szavaiddal, pl. „minden ExMachina-levél a Payneum része”.</p>}
      {shown.map((g) => (
        <div key={g.id} className={`guide ${g.status}`}>
          {edit && edit.id === g.id
            ? <form className="j-row" onSubmit={(e) => { e.preventDefault(); set(g.id, { text: edit.text }); setEdit(null); }}><input className="j-input" value={edit.text} onChange={(e) => setEdit({ id: g.id, text: e.target.value })} /><button className="j-btn">Mentés</button></form>
            : <div>{g.text}{g.status === 'proposed' && g.because && <div className="w">ebből tanultam: {g.because}</div>}</div>}
          <div className="task-acts">
            {g.status === 'proposed' && <button className="j-btn j-btn--primary" onClick={() => set(g.id, { status: 'active' })}>Igen, jegyezd meg</button>}
            {g.status === 'off' ? <button className="j-btn" onClick={() => set(g.id, { status: 'active' })}>Visszakapcsol</button>
              : <button className="j-btn" onClick={() => set(g.id, { status: 'off' })}>{g.status === 'proposed' ? 'Nem' : 'Kikapcsol'}</button>}
            {g.status !== 'off' && <button className="j-btn" onClick={() => setEdit({ id: g.id, text: g.text })}>Átírom</button>}
          </div>
        </div>
      ))}
      {!onlyProposed && <form className="j-row quick-form" onSubmit={(e) => { e.preventDefault(); if (text.trim()) run(api('/api/guidance', { text }).then(() => setText(''))); }}>
        <input className="j-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Új utasítás…" /><button className="j-btn j-btn--primary">+</button></form>}
      {err && <p className="err small">{err}</p>}
    </Panel>
  );
}

/** A thread's Claude workspaces: a project folder (Claude Code hooks) or a Desktop project (the Jarvis connector).
 *  The key is shown once, with what to run. */
export function Workspaces({ threadId, list, reload }: { threadId: string; list: any[]; reload: () => void }) {
  const [kind, setKind] = useState<'folder' | 'desktop'>('folder');
  const [label, setLabel] = useState('');
  const [made, setMade] = useState<{ token: string; kind: string; label: string } | null>(null);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(false);
  const create = () => api<{ token: string }>(`/api/threads/${threadId}/workspaces`, { kind, label })
    .then((r) => { setMade({ token: r.token, kind, label }); setLabel(''); reload(); }).catch((e) => setErr(e.message));
  const revoke = (id: string) => api(`/api/workspaces/${id}`, undefined, { method: 'DELETE' }).then(reload).catch((e) => setErr(e.message));
  const url = location.origin;
  const desktop = (t: string, l: string) => JSON.stringify({ mcpServers: { jarvis: { command: 'node', args: ['C:\\Project.OWN\\jarvis2\\tools\\claude\\jarvis-mcp.mjs'], env: { JARVIS_URL: url, JARVIS_WORKSPACES: JSON.stringify({ [l]: t }) } } } }, null, 2);
  return (
    <Panel c="amber"><Label c="amber">{list.length ? <><ClaudeMark size={14} /> Claude-munkaterület</> : 'Claude-munkaterület'}</Label>
      {list.map((w) => (
        <div key={w.id} className="j-slot">
          <div className="t">{w.kind === 'folder' ? 'mappa' : 'Desktop'}</div>
          <div><div><b>{w.label}</b></div><div className="w">{w.last_seen ? `utoljára ${day(w.last_seen)}${w.last_machine ? ` · ${w.last_machine}` : ''}` : 'még nem jelentkezett'}</div>
            <div className="task-acts"><button className="j-btn" onClick={() => revoke(w.id)}>Kulcs visszavonása</button></div></div>
        </div>
      ))}
      {!list.length && !open && <p className="small muted">Ha ehhez az ügyhöz egy projektmappában (Claude Code) vagy egy Desktop-projektben dolgozunk, kösd össze: a munkamenet a Jarvis-állapottal indul, a döntések visszakerülnek ide, és amit a Jarvisnak mondasz a projektről, azt a projekt megkapja.</p>}
      {made ? (
        <div className="money-form">
          <p><b>A kulcs csak most látszik</b> – tedd a Vaultwardenbe („Jarvis munkaterület: {made.label}”), a másik gépen is ez kell.</p>
          {made.kind === 'folder' ? <>
            <p className="small">A projektmappa gyökerében (mindkét gépen egyszer) futtasd – a mappa összes almappája és git-repója ehhez a szálhoz tartozik, a hook a felhasználói beállításba kerül:</p>
            <pre className="cmd">{String.raw`node C:\Project.OWN\jarvis2\tools\claude\jarvis-hook.mjs link ` + `${url} ${made.token}`}</pre>
          </> : <>
            <p className="small">A Claude Desktop beállításaiban (Settings → Developer → Edit Config, claude_desktop_config.json) add hozzá, majd indítsd újra az appot. A Desktop-projekt utasításába írd: „A munka elején hívd a jarvis_context, a végén a jarvis_log eszközt (munkaterület: {made.label}).”</p>
            <pre className="cmd">{desktop(made.token, made.label)}</pre>
          </>}
          <button className="j-btn" onClick={() => { navigator.clipboard.writeText(made.token); }}>Kulcs másolása</button>
          <button className="j-btn linkish" onClick={() => { setMade(null); setOpen(false); }}>Kész</button>
        </div>
      ) : open ? (
        <form className="money-form" onSubmit={(e) => { e.preventDefault(); create(); }}>
          <label>Fajta<select className="j-input" value={kind} onChange={(e) => setKind(e.target.value as any)}><option value="folder">Projektmappa (Claude Code)</option><option value="desktop">Desktop-projekt (Claude app)</option></select></label>
          <label>{kind === 'folder' ? 'Mappa' : 'Név'}<input className="j-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder={kind === 'folder' ? 'C:\\Project.Payneum' : 'Ausztriai út'} /></label>
          <div className="j-row"><button className="j-btn j-btn--primary">Összekötés</button><button type="button" className="j-btn linkish" onClick={() => setOpen(false)}>Mégse</button></div>
        </form>
      ) : <button className="j-btn" onClick={() => setOpen(true)}>{list.length ? '+ Még egy munkaterület' : 'Összekötés egy Claude-munkaterülettel'}</button>}
      {err && <p className="err small">{err}</p>}
    </Panel>
  );
}

export { plus };
